import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './ServiceTemplates.css';
import './DetailPages.css';

function ServiceTemplateDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [template, setTemplate] = useState(null);
  const [hourlyRates, setHourlyRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadTemplate(),
        loadHourlyRates()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadTemplate = async () => {
    try {
      const data = await api.get(`/service-templates/${id}`, tenantId);
      setTemplate(data);
    } catch (error) {
      console.error('Error loading template:', error);
      showNotification('Fehler beim Laden des Standardauftrags', 'error');
      navigate('/service-templates');
    }
  };

  const loadHourlyRates = async () => {
    try {
      const data = await api.get('/hourly-rates', tenantId);
      setHourlyRates(data);
    } catch (error) {
      console.error('Error loading hourly rates:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getHourlyRate = (hourlyRateId) => {
    const rate = hourlyRates.find(r => r.id === hourlyRateId);
    return rate ? rate.rate : 0;
  };

  if (loading) {
    return <Spinner />;
  }

  if (!template) {
    return <div>Standardauftrag nicht gefunden</div>;
  }

  const materials = template.materials || [];
  const labor = template.labor || [];
  const totalMaterialsAmount = materials.reduce((sum, item) => sum + (item.amount || 0) * (item.quantity || 1), 0);
  let totalLaborAmount = 0;
  for (const lab of labor) {
    if (lab.hours && lab.hourlyRateId) {
      totalLaborAmount += (lab.hours || 0) * getHourlyRate(lab.hourlyRateId);
    }
  }

  return (
    <div className="template-detail">
      <div className="detail-header">
        <button onClick={() => navigate('/service-templates')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{template.name}</h1>
        <button onClick={() => navigate(`/service-templates/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>


      {materials.length > 0 && (
        <div className="detail-section">
          <h2>Materialien</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Menge</th>
                <th>Einzelpreis</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((item, idx) => (
                <tr key={idx}>
                  <td>{item.description || '-'}</td>
                  <td>{item.quantity || 1}</td>
                  <td>{formatCurrency(item.amount || 0)}</td>
                  <td>{formatCurrency((item.amount || 0) * (item.quantity || 1))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Materialien:</strong></td>
                <td><strong>{formatCurrency(totalMaterialsAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {labor.length > 0 && (
        <div className="detail-section">
          <h2>Arbeitseinsatz</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Stunden</th>
                <th>Stundensatz</th>
                <th>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {labor.map((lab, idx) => {
                const rate = getHourlyRate(lab.hourlyRateId);
                const amount = (lab.hours || 0) * rate;
                return (
                  <tr key={idx}>
                    <td>{lab.description || '-'}</td>
                    <td>{lab.hours || 0}</td>
                    <td>{formatCurrency(rate)}/h</td>
                    <td>{formatCurrency(amount)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Zwischensumme Arbeitseinsatz:</strong></td>
                <td><strong>{formatCurrency(totalLaborAmount)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <div className="detail-section">
        <h2>Gesamtbetrag</h2>
        <p><strong>{formatCurrency(totalMaterialsAmount + totalLaborAmount)}</strong></p>
      </div>
    </div>
  );
}

export default ServiceTemplateDetail;

